import React from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { logger } from '../logging';
import { shipmentApi } from '../api/shipment.api';
import { CARRIERS } from '../forms/constants';
import { useExcelWorkerApi } from './useExcelWorkerApi';

const carrierOptions = CARRIERS.map((carrier) => ({
  label: `${carrier.scac} - ${carrier.name} - ${carrier.tier}`,
  carrier,
  value: carrier.scac,
}));

type Options = {
  isBGEmployee: boolean;
  enterpriseID?: number;
  populateAccounts?: boolean;
};

export const useFullFormatDetection = (
  watch: UseFormWatch<any>,
  setValue: UseFormSetValue<any>,
  { isBGEmployee, enterpriseID, populateAccounts = true }: Options
) => {
  const excelWorkerApi = useExcelWorkerApi();
  const [loadingFromFile, setLoadingFromFile] = React.useState(false);
  const [loadedFromFile, setLoadedFromFile] = React.useState(false);

  const watchedFile = watch('shipmentsFileUpload');

  React.useEffect(() => {
    if (!watchedFile?.length) {
      setLoadedFromFile(false);
      return;
    }

    (async () => {
      setLoadingFromFile(true);
      setLoadedFromFile(false);
      try {
        const config = await excelWorkerApi.parseFullFormatFirstRow(watchedFile);
        if (!config) return;

        if (populateAccounts && config.accountNumbers.length) {
          const accountLookups = await Promise.all(
            config.accountNumbers.map(async (accountNumber) => {
              try {
                if (isBGEmployee) {
                  const results = await shipmentApi.enterpriseSearch(accountNumber);
                  return results.find((e) => e.accountNumber === accountNumber) ?? null;
                } else if (enterpriseID != null) {
                  const children = await shipmentApi.getChildEnterpriseOptions(enterpriseID);
                  return children.find((e) => e.accountNumber === accountNumber) ?? null;
                }
                return null;
              } catch {
                return null;
              }
            })
          );
          const validAccounts = accountLookups.filter(Boolean);
          if (validAccounts.length) setValue('accounts', validAccounts as any);
        }

        const matchedCarriers = config.targetedCarrierScacs
          .map((scac) => carrierOptions.find((opt) => opt.value === scac))
          .filter(Boolean);
        if (matchedCarriers.length) setValue('targetedCarriers', matchedCarriers as any);

        setValue('returnCustomerRates', config.returnCustomerRates);
        setValue('returnLiveContractRates', config.returnLiveContractRates);
        setValue('returnProposalContractRates', config.returnProposalContractRates);
        setValue('returnVolumeRates', config.returnVolumeRates);

        if (isBGEmployee && config.notifyEmails.length) {
          setValue('notifyEmails', config.notifyEmails.map((v) => ({ value: v })));
        }

        setLoadedFromFile(true);
      } catch (e) {
        logger.error(`Failed to parse full-format file: ${e}`);
      } finally {
        setLoadingFromFile(false);
      }
    })();
  }, [watchedFile, excelWorkerApi, isBGEmployee, enterpriseID, populateAccounts, setValue]);

  return { loadingFromFile, loadedFromFile };
};
